import { FilterOperator } from 'types/filters';

/**
 * Alias map from condition-system shorthand operators to canonical FilterOperator names.
 */
const OPERATOR_ALIASES: Record<string, FilterOperator> = {
  gt: 'greater_than',
  gte: 'greater_than_equals',
  lt: 'less_than',
  lte: 'less_than_equals',
  eq: 'equals',
  neq: 'not_equals',
  not_empty: 'is_not_empty',
};

/**
 * Normalize an operator string to its canonical FilterOperator name.
 * Maps condition-system aliases (gt, gte, lt, lte, eq, neq, not_empty)
 * to their canonical equivalents. Returns the input unchanged if already canonical.
 */
export function normalizeOperator(op: string): FilterOperator {
  return OPERATOR_ALIASES[op] ?? (op as FilterOperator);
}

/**
 * Select the operand `compareValues` should compare against for a given
 * FilterExpression.
 *
 * `in` / `not_in` / `contains_all` carry their operand in the `values`
 * ARRAY; every other operator carries it in the scalar `value`. Passing
 * `value` for an `in` filter hands `compareValues` an `undefined` — which is
 * not an array, so it returns false and the filter silently never matches.
 *
 * This is the single source of truth for that rule so the in-memory evaluator
 * (`filterEvaluation`) and the Gremlin-backed engine (graph-service
 * `filterEngine`) cannot drift apart on it again.
 */
export function expectedOperand(expression: {
  operator: FilterOperator;
  value?: unknown;
  values?: unknown[];
}): unknown {
  return expression.operator === 'in' || expression.operator === 'not_in' || expression.operator === 'contains_all'
    ? expression.values
    : expression.value;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Milliseconds since epoch for a date-like value, or null when it does not parse. */
function dateMs(value: unknown): number | null {
  const ms = new Date(String(value)).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * A finite integer >= 1, or null. An invalid day count never matches (fail
 * closed), never coerces (e.g. NaN/negative silently becoming 0).
 */
function dayCount(value: unknown): number | null {
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 ? n : null;
}

/** Strictly past: within the last `days` days, inclusive of now. A future date does NOT match. */
function withinLastDays(actual: unknown, days: number): boolean {
  const ms = dateMs(actual);
  if (ms === null) return false;
  const daysAgo = (Date.now() - ms) / DAY_MS;
  return daysAgo >= 0 && daysAgo <= days;
}

/** Strictly future — the mirror of `withinLastDays`. */
function withinNextDays(actual: unknown, days: number): boolean {
  const ms = dateMs(actual);
  if (ms === null) return false;
  const daysAhead = (ms - Date.now()) / DAY_MS;
  return daysAhead >= 0 && daysAhead <= days;
}

const bothNumbers = (a: unknown, e: unknown): a is number => typeof a === 'number' && typeof e === 'number';

/**
 * A strict ISO 8601 calendar date, optionally with a time and an offset:
 * `2026-01-01`, `2026-01-01T10:30`, `2026-01-01T10:30:00.000Z`,
 * `2026-01-01T10:30:00+01:00`. Deliberately strict — `Date.parse` also accepts
 * loose forms (`'2026'`, `'Jan 5 2026'`), and coercing one of those into an
 * instant would let a non-date string order against a date, widening the
 * filter. Anything this does not match is not a date operand.
 */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})?)?$/;

/**
 * The instant an ISO 8601 date operand denotes, or null when the value is not
 * one. Ordering compares INSTANTS, not characters: lexicographic order equals
 * chronological order only for same-shape `Z`-normalised strings, and a stored
 * value can legitimately be a shorter shape (`2026-01-01`) or carry a non-zero
 * offset (`2026-01-01T01:00:00+01:00`), both of which compare wrongly as text.
 * Parsing normalises all three shapes onto one axis.
 *
 * A time with no offset is read as UTC (the canonical representation is
 * `Z`-suffixed, and Elasticsearch's `date` mapping assumes UTC for a zoneless
 * value) rather than as the host's local time, so the result never depends on
 * the machine the evaluator runs on.
 */
function dateInstant(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const match = ISO_DATE_PATTERN.exec(value);
  if (!match) return null;
  const hasTime = match[1] !== undefined;
  const hasZone = match[2] !== undefined;
  const ms = Date.parse(hasTime && !hasZone ? `${value}Z` : value);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Build one of the four ordering comparators. Numbers compare as numbers,
 * exactly as before; two ISO 8601 date operands compare as instants, matching
 * Gremlin's `P.gt`/`P.lt`/`P.gte`/`P.lte` and Elasticsearch's `range` clause
 * so the three engines agree on date ordering. Everything else — a
 * non-numeric, non-date operand on either side, or a number paired with a date
 * (a representation disagreement, as with `between`) — fails closed.
 */
const ordering =
  (compare: (a: number, b: number) => boolean): Comparator =>
  (a, e) => {
    if (bothNumbers(a, e)) return compare(a, e as number);
    const actualMs = dateInstant(a);
    const expectedMs = dateInstant(e);
    return actualMs !== null && expectedMs !== null && compare(actualMs, expectedMs);
  };

const bothStrings = (a: unknown, e: unknown): a is string => typeof a === 'string' && typeof e === 'string';

/**
 * Substring / element containment for a string or an array; `null` when
 * `actual` is neither, so `contains` and `not_contains` can both fail closed
 * on an absent actual (see notContainsIsFalseWhenActualAbsent).
 */
function containsValue(actual: unknown, expected: unknown): boolean | null {
  if (bothStrings(actual, expected)) return actual.includes(expected as string);
  if (Array.isArray(actual)) return actual.includes(expected);
  return null;
}

const isEmptyValue = (actual: unknown): boolean =>
  actual === undefined || actual === null || actual === '' || (Array.isArray(actual) && actual.length === 0);

type Comparator = (
  actual: unknown,
  expected: unknown,
  options?: { min?: number | string; max?: number | string }
) => boolean;

/**
 * One predicate per canonical operator. The `Record` is the exhaustiveness
 * guard: a FilterOperator added to types/filters.d.ts without an entry here is
 * a compile error, not a silent runtime `false`.
 */
const COMPARATORS: Record<FilterOperator, Comparator> = {
  equals: (a, e) => a === e || String(a) === String(e),
  not_equals: (a, e) => a !== e && String(a) !== String(e),
  greater_than: ordering((a, b) => a > b),
  less_than: ordering((a, b) => a < b),
  greater_than_equals: ordering((a, b) => a >= b),
  less_than_equals: ordering((a, b) => a <= b),
  contains: (a, e) => containsValue(a, e) ?? false,
  // Mirrors `contains`, negated — but an absent actual is `false`, never
  // `true`. Negating `contains`'s own `false` (no string/array to search)
  // would silently match every sphere missing the property.
  not_contains: (a, e) => {
    const contained = containsValue(a, e);
    return contained === null ? false : !contained;
  },
  contains_all: (a, e) => Array.isArray(a) && Array.isArray(e) && e.every((v) => a.includes(v)),
  in: (a, e) => Array.isArray(e) && e.includes(a),
  not_in: (a, e) => Array.isArray(e) && !e.includes(a),
  exists: (a) => a !== undefined && a !== null,
  not_exists: (a) => a === undefined || a === null,
  starts_with: (a, e) => bothStrings(a, e) && a.startsWith(e as string),
  ends_with: (a, e) => bothStrings(a, e) && a.endsWith(e as string),
  is_true: (a) => a === true,
  is_false: (a) => a === false,
  is_empty: (a) => isEmptyValue(a),
  is_not_empty: (a) => !isEmptyValue(a),
  // Numeric bounds compare numerically; date bounds are ISO 8601 UTC strings
  // (Phase 7a, EAINT-931) — fixed millisecond precision makes lexicographic
  // order equal chronological order, so a plain string comparison suffices.
  // Mixed-type operands (a number against a string bound, or vice versa)
  // never match — that pairing means the bound and the actual disagree on
  // representation, which is a bug upstream, not a coercible comparison.
  between: (a, _e, options) => {
    const min = options?.min;
    const max = options?.max;
    if (typeof a === 'number' && typeof min === 'number' && typeof max === 'number') return a >= min && a <= max;
    if (typeof a === 'string' && typeof min === 'string' && typeof max === 'string') return a >= min && a <= max;
    return false;
  },
  // Symmetric (past or future): the absolute day difference.
  date_within_days: (a, e) => {
    const ms = dateMs(a);
    return ms !== null && Math.abs(Date.now() - ms) / DAY_MS <= Number(e);
  },
  // Generalises `last_7_days`/`last_30_days` to an arbitrary N — unlike
  // `date_within_days`, a future date does NOT match (withinLastDaysMatchesOnlyPast).
  within_last_days: (a, e) => {
    const n = dayCount(e);
    return n !== null && withinLastDays(a, n);
  },
  within_next_days: (a, e) => {
    const n = dayCount(e);
    return n !== null && withinNextDays(a, n);
  },
  last_7_days: (a) => withinLastDays(a, 7),
  last_30_days: (a) => withinLastDays(a, 30),
};

/**
 * Every operator the in-memory engine can actually evaluate — the `COMPARATORS`
 * key set, read at module load. Exposed (read-only; no behaviour of its own) so a
 * consumer that OFFERS operators to a user — `getFilterOperatorOptions` in the
 * design system — can be asserted against the engine that has to run them, rather
 * than against the `FilterOperator` union, which says only that a key exists in
 * the type. An operator a picker offers but no engine implements is a filter that
 * silently matches nothing.
 */
export const SUPPORTED_FILTER_OPERATORS: readonly FilterOperator[] = Object.freeze(
  Object.keys(COMPARATORS) as FilterOperator[]
);

/**
 * Compare a value against an expected value using a FilterOperator.
 * This is the single source of truth for all operator evaluation across the platform.
 *
 * Aliases are canonicalised HERE rather than at each call site (EAINT-894 review pass 2).
 * Phase 7.8 taught the cost of the alternative: `normalizeOperator` was applied at three of the
 * many places an operator reaches the `COMPARATORS` lookup, so a stored `gte` fell through to
 * `undefined` and evaluated FALSE forever — a calculated transition that could never fire.
 * Normalising at the boundary of the lookup itself makes "the caller forgot" unrepresentable.
 *
 * @param actual - The value to test
 * @param operator - The FilterOperator, canonical or an authoring alias (`gte`, `eq`, …)
 * @param expected - The expected/comparison value (not needed for unary operators like exists, is_true, etc.)
 *                   For `in`/`not_in` this MUST be the `values` array — use `expectedOperand()`.
 * @param options - Additional parameters for range operators (between uses min/max)
 */
export function compareValues(
  actual: unknown,
  operator: FilterOperator,
  expected?: unknown,
  options?: { min?: number | string; max?: number | string },
): boolean {
  const compare = COMPARATORS[normalizeOperator(operator as string)];
  // An operator outside the canonical union (a raw string at runtime) matches nothing.
  return compare ? compare(actual, expected, options) : false;
}
